import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FlexLayoutModule, LayoutGapStyleBuilder } from '@ngbracket/ngx-layout';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import { PopupService, PopupModule } from '@progress/kendo-angular-popup';
import { ResizeBatchService } from '@progress/kendo-angular-common';
import { GridModule } from '@progress/kendo-angular-grid';
import { PagerModule } from '@progress/kendo-angular-pager';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { TreeListModule } from '@progress/kendo-angular-treelist';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { BASE_PATH as ERP_BASE_PATH } from '@api-client';
import { BASE_PATH as ACCOUNTING_BASE_PATH } from '@api-accounting-client/variables';
// Importing hammerjs in order to make plot area points to to be clickable by left click button, on scatter plot
import 'hammerjs';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ApiModule as CoreModule } from '@api-client/api.module';
import { ApiModule as AccountingModule } from '@api-accounting-client/api.module';
import {
  UserStateConfigurationsService,
  ProductsService,
  SalesInvoicesService,
  POSService,
  PaymentsService as PaymentsReportsService,
  ReceiptsService,
  StockLocationService,
  TransferOrdersService,
  CreditNotesService,
  JournalsService,
  CustomerMetricsService
} from '@api-reports-client';
import { reportsServiceProvider } from '@app/shared/service-providers';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { MessageService } from '@progress/kendo-angular-l10n';

import {
  DeleteDialogComponent,
  WarningDialogComponent
} from './shared/components/dialogs/warning';
import {
  HttpErrorHandlerService,
  ExceptionLoggingService
} from './shared/services/error';
import { ShellModule } from './shell/shell.module';
import { ServerHttpInterceptor } from './shared/interceptors/server-http.interceptor';
import { BillingLockInterceptor } from './shared/interceptors/billing-lock.interceptor';
import { BillingLockService } from './core/payment-popup/billing-lock.service';
import { SignalRTopicInterceptor } from './shared/interceptors/signalr-topic.interceptor';
import { SignalRConnectionService } from './shared/services/signalr-connection.service';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CustomLayoutGapStyleBuilderService } from './shared/services';
import { UsersnapComponent } from './shared/components/usersnap/usersnap.component';
import { PaymentPopupComponent } from './core/payment-popup/payment-popup.component';
import { AnnouncementHostComponent } from './core/announcements/announcement-host.component';
import { AuthenticationDynamicModule } from './auth/authentication-dynamic.module';
import { AppHostComponent } from './integrations/app-host/app-host.component';
import {
  EnvConfigLoaderService,
  AppNavigationService,
  AppInsightsService
} from './shared/services/app';

export function appInitializerERPBase(config: EnvConfigLoaderService): any {
  return config.getSettings('env').erpApiUrl;
}

export function appInitializerAccountingBase(config: EnvConfigLoaderService): any {
  return config.getSettings('env').accountingApiUrl;
}

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

@NgModule({
  declarations: [
    AppComponent,
    WarningDialogComponent,
    DeleteDialogComponent,
    UsersnapComponent,
    AppHostComponent
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ShellModule,
    NavigationModule,
    ButtonsModule,
    BrowserAnimationsModule,
    FlexLayoutModule,
    LoaderModule,
    CoreModule,
    AccountingModule,
    GridModule,
    PagerModule,
    DateInputsModule,
    DialogsModule,
    NotificationModule,
    TreeListModule,
    PopupModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    // AuthenticationModule
    AuthenticationDynamicModule.forRoot('env-config.json'),
    PaymentPopupComponent,
    AnnouncementHostComponent
  ],
  providers: [
    PopupService,
    ResizeBatchService,
    MessageService,
    HttpErrorHandlerService,
    AppNavigationService,
    {
      provide: LayoutGapStyleBuilder,
      useClass: CustomLayoutGapStyleBuilderService
    },
    {
      provide: ERP_BASE_PATH,
      useFactory: appInitializerERPBase,
      deps: [EnvConfigLoaderService],
      multi: true
    },
    {
      provide: ACCOUNTING_BASE_PATH,
      useFactory: appInitializerAccountingBase,
      deps: [EnvConfigLoaderService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerHttpInterceptor,
      multi: true,
      deps: [HttpErrorHandlerService, EnvConfigLoaderService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SignalRTopicInterceptor,
      multi: true,
      deps: [SignalRConnectionService]
    },
    {
      // ZEY-3046: 402 { code: 'billing_locked' } from any API → "Account suspended" screen.
      provide: HTTP_INTERCEPTORS,
      useClass: BillingLockInterceptor,
      multi: true,
      deps: [BillingLockService]
    },
    {
      provide: ErrorHandler,
      useClass: ExceptionLoggingService,
      deps: [AppInsightsService]
    },
    {
      provide: ICON_SETTINGS,
      useValue: { type: 'font' }
    },
    ...reportsServiceProvider([
      UserStateConfigurationsService,
      ProductsService,
      SalesInvoicesService,
      POSService,
      PaymentsReportsService,
      ReceiptsService,
      StockLocationService,
      TransferOrdersService,
      CreditNotesService,
      JournalsService,
      CustomerMetricsService
    ]),
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
